import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useLanguage } from '../../context/LanguageContext';
import { MenuIcon, X, ChevronDown } from 'lucide-react';
import Button from '../ui/Button';

const Header: React.FC = () => {
  const { t, i18n } = useTranslation();
  const { currentLanguage, changeLanguage, getLocalizedPath } = useLanguage();
  const location = useLocation();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [isLanguageDropdownOpen, setIsLanguageDropdownOpen] = useState(false);
  const languageMenuRef = useRef<HTMLDivElement>(null);

  const toggleMenu = () => setIsMenuOpen(!isMenuOpen);
  const closeMenu = () => setIsMenuOpen(false);

  const toggleLanguageDropdown = () => {
    setIsLanguageDropdownOpen(!isLanguageDropdownOpen);
  };

  const handleLanguageChange = (lang: 'en' | 'fr') => {
    changeLanguage(lang);
    setIsLanguageDropdownOpen(false);
    closeMenu();
  };

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    if (!isLanguageDropdownOpen) return;

    const onPointerDown = (e: PointerEvent) => {
      if (
        languageMenuRef.current &&
        !languageMenuRef.current.contains(e.target as Node)
      ) {
        setIsLanguageDropdownOpen(false);
      }
    };

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setIsLanguageDropdownOpen(false);
    };

    document.addEventListener('pointerdown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [isLanguageDropdownOpen]);

  const navItems = [
    { name: t('navigation.home'), path: '/' },
    { name: t('navigation.services'), path: '/services' },
    { name: t('navigation.pricing'), path: '/pricing' },
    { name: t('navigation.about'), path: '/about' },
    { name: t('navigation.contact'), path: '/contact' },
    { name: t('navigation.faq'), path: '/faq' },
  ];

  return (
    <header
      className={`fixed left-0 right-0 top-0 z-50 pt-[env(safe-area-inset-top,0px)] transition-all duration-300 ${
        isScrolled
          ? 'border-b border-slate-200/80 bg-white/95 py-2.5 shadow-sm backdrop-blur-md'
          : 'bg-transparent py-3 sm:py-4'
      }`}
    >
      <div className="container-custom flex items-center justify-between gap-3">
        <Link
          to={getLocalizedPath('/')}
          className="flex min-h-[44px] min-w-[44px] shrink-0 items-center justify-center touch-manipulation [-webkit-tap-highlight-color:transparent]"
          onClick={closeMenu}
        >
          <img
            src="/logo.png"
            alt={t('site.logoAlt')}
            className="h-12 w-12 md:h-14 md:w-14 object-contain"
          />
        </Link>

        <nav className="hidden md:flex items-center gap-8">
          {navItems.map((item) => {
            const localizedPath = getLocalizedPath(item.path);
            const isActive = location.pathname === localizedPath;

            return (
              <Link
                key={item.path}
                to={localizedPath}
                className={`text-sm font-medium transition-colors ${
                  isActive
                    ? 'text-primary'
                    : 'text-slate-600 hover:text-primary'
                }`}
              >
                {item.name}
              </Link>
            );
          })}
        </nav>

        <div className="hidden md:flex items-center gap-4">
          <div className="relative" ref={languageMenuRef}>
            <button
              type="button"
              onClick={toggleLanguageDropdown}
              aria-expanded={isLanguageDropdownOpen}
              aria-haspopup="listbox"
              className="flex items-center gap-1 rounded-lg px-2 py-1.5 text-sm text-slate-600 transition-colors hover:bg-primary-light/60 hover:text-primary focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2"
            >
              <span>{currentLanguage === 'en' ? 'EN' : 'FR'}</span>
              <ChevronDown size={16} className="opacity-70" />
            </button>

            {isLanguageDropdownOpen && (
              <div
                className="absolute right-0 mt-2 w-40 overflow-hidden rounded-lg border border-slate-200/90 bg-white py-1 shadow-lg z-20"
                role="listbox"
              >
                <button
                  type="button"
                  role="option"
                  aria-selected={currentLanguage === 'en'}
                  onClick={() => handleLanguageChange('en')}
                  className={`w-full px-4 py-2.5 text-left text-sm transition-colors hover:bg-primary-light/50 ${
                    currentLanguage === 'en' ? 'bg-primary-light/70 text-primary font-medium' : ''
                  }`}
                >
                  {t('languages.en')}
                </button>
                <button
                  type="button"
                  role="option"
                  aria-selected={currentLanguage === 'fr'}
                  onClick={() => handleLanguageChange('fr')}
                  className={`w-full px-4 py-2.5 text-left text-sm transition-colors hover:bg-primary-light/50 ${
                    currentLanguage === 'fr' ? 'bg-primary-light/70 text-primary font-medium' : ''
                  }`}
                >
                  {t('languages.fr')}
                </button>
              </div>
            )}
          </div>

          <Button
            as={Link}
            to={getLocalizedPath('/contact')}
            variant="primary"
            size="sm"
          >
            {t('hero.cta')}
          </Button>
        </div>

        <button
          type="button"
          className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg text-slate-600 touch-manipulation hover:bg-primary-light/50 hover:text-primary focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 md:hidden [-webkit-tap-highlight-color:transparent]"
          onClick={toggleMenu}
          aria-expanded={isMenuOpen}
          aria-label={isMenuOpen ? t('navigation.closeMenu') : t('navigation.openMenu')}
        >
          {isMenuOpen ? <X size={24} /> : <MenuIcon size={24} />}
        </button>
      </div>

      {isMenuOpen && (
        <div className="max-h-[min(70vh,calc(100dvh-5rem))] overflow-y-auto overscroll-contain border-t border-slate-200/80 bg-white shadow-sm animate-slide-down md:hidden">
          <div className="container-custom py-4 pb-[max(1.5rem,env(safe-area-inset-bottom,0px))]">
            <nav className="flex flex-col gap-0.5">
              {navItems.map((item) => {
                const localizedPath = getLocalizedPath(item.path);
                const isActive = location.pathname === localizedPath;

                return (
                  <Link
                    key={item.path}
                    to={localizedPath}
                    className={`touch-manipulation rounded-xl px-3 py-3.5 text-base font-medium transition-colors active:bg-slate-100 ${
                      isActive
                        ? 'bg-primary-light/70 text-primary'
                        : 'text-slate-700 hover:bg-slate-50'
                    }`}
                    onClick={closeMenu}
                  >
                    {item.name}
                  </Link>
                );
              })}

              <div className="mt-4 border-t border-slate-200 pt-4">
                <p className="mb-2 text-xs font-medium uppercase tracking-wide text-slate-500">
                  {i18n.language === 'fr' ? 'Langue' : 'Language'}
                </p>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => handleLanguageChange('en')}
                    className={`min-h-11 flex-1 touch-manipulation rounded-lg py-2.5 text-sm font-medium ${
                      currentLanguage === 'en'
                        ? 'bg-primary text-white'
                        : 'bg-slate-100 text-slate-700'
                    }`}
                  >
                    EN
                  </button>
                  <button
                    type="button"
                    onClick={() => handleLanguageChange('fr')}
                    className={`min-h-11 flex-1 touch-manipulation rounded-lg py-2.5 text-sm font-medium ${
                      currentLanguage === 'fr'
                        ? 'bg-primary text-white'
                        : 'bg-slate-100 text-slate-700'
                    }`}
                  >
                    FR
                  </button>
                </div>
              </div>

              <Button
                as={Link}
                to={getLocalizedPath('/contact')}
                variant="primary"
                fullWidth
                className="mt-4"
                onClick={closeMenu}
              >
                {t('hero.cta')}
              </Button>
            </nav>
          </div>
        </div>
      )}
    </header>
  );
};

export default Header;
